/* global runtime */
// Import og komplett eksport av regnskapsdata.
// Lastes som klassisk script før backend.jsx og deler dens bindingsmiljø.
const hashFileSha256 = async (file) => {
  if (!file?.arrayBuffer || !window.crypto?.subtle) return '';
  const buffer = await file.arrayBuffer();
  const digest = await window.crypto.subtle.digest('SHA-256', buffer);
  return Array.from(new Uint8Array(digest)).map((byte) => byte.toString(16).padStart(2, '0')).join('');
};
const assertAccountingImportFile = (file) => {
  if (!file) throw new Error(t('svc.pickExportFile'));
  const extension = String(file.name || '').split('.').pop().toLowerCase();
  if (extension === 'xls') throw new Error(t('svc.unsupportedAccountingImport'));
  const isAllowed = ['zip', 'xml', 'csv', 'xlsx'].includes(extension) || ACCOUNTING_IMPORT_MIME_TYPES.has(file.type || '');
  if (!isAllowed) throw new Error(t('svc.unsupportedAccountingImport'));
  if (Number(file.size || 0) <= 0 || Number(file.size || 0) > 2 * 1024 * 1024 * 1024) {
    throw new Error(t('svc.fileSize2gb'));
  }
};

const accountingImportContentType = (file) => {
  const name = String(file?.name || '').toLowerCase();
  if (file?.type) return file.type;
  if (name.endsWith('.xml')) return 'application/xml';
  if (name.endsWith('.csv')) return 'text/csv';
  if (name.endsWith('.xlsx')) return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
  return 'application/zip';
};

const uploadAccountingImportFile = async ({ businessId = runtime.activeBusinessId, file, onProgress } = {}) => {
  await initFirebase();
  if (!runtime.storage) throw new Error(t('svc.noStorage'));
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  assertAccountingImportFile(file);
  window.PlummeAccountingJournalRecovery.assertRequestAllowed(runtime, 'createAccountingImportJobHttp', { businessId });
  onProgress?.({ phase: 'uploading', bytesTransferred: 0, totalBytes: file.size || 0, percent: 0 });
  const id = createId('accounting-import');
  const cleanFileName = safeFilePart(file.name || 'regnskap-eksport.zip');
  const hash = await hashFileSha256(file).catch(() => '');
  const storagePath = `businesses/${businessId}/accountingImports/${id}/${Date.now()}-${cleanFileName}`;
  const ref = runtime.storage.ref().child(storagePath);
  const uploadTask = ref.put(file, {
    contentType: accountingImportContentType(file),
    contentDisposition: `inline; filename*=UTF-8''${encodeURIComponent(file.name || cleanFileName)}`,
    customMetadata: {
      businessId,
      originalName: file.name || cleanFileName,
      source: 'plumme-accounting-import',
    },
  });
  await new Promise((resolve, reject) => {
    uploadTask.on('state_changed', (snapshot) => {
      const totalBytes = snapshot.totalBytes || file.size || 0;
      const bytesTransferred = snapshot.bytesTransferred || 0;
      const percent = totalBytes > 0 ? Math.max(0, Math.min(100, Math.round((bytesTransferred / totalBytes) * 100))) : 0;
      onProgress?.({ phase: 'uploading', bytesTransferred, totalBytes, percent });
    }, reject, resolve);
  });
  onProgress?.({ phase: 'uploaded', bytesTransferred: file.size || 0, totalBytes: file.size || 0, percent: 100 });
  const downloadUrl = await ref.getDownloadURL().catch(() => '');
  return {
    id,
    name: file.name || cleanFileName,
    mimeType: accountingImportContentType(file),
    size: file.size || 0,
    storagePath,
    hash: hash ? `sha256:${hash}` : undefined,
    downloadUrl: downloadUrl || undefined,
    uploadedAt: nowIso(),
  };
};

const createAccountingImportJob = async ({ businessId = runtime.activeBusinessId, file, files, source = 'complete-accounting-export', onProgress } = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  const inputFiles = safeArray(files).length ? safeArray(files) : (file ? [file] : []);
  onProgress?.({ phase: 'analyzing', percent: 100 });
  const payload = await authorizedPost('createAccountingImportJobHttp', { businessId, file: inputFiles[0] || file, files: inputFiles, source });
  onProgress?.({ phase: 'analyzed', percent: 100 });
  return payload.job || null;
};

const getLatestAccountingImportJob = async ({ businessId = runtime.activeBusinessId } = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  const payload = await authorizedPost('getLatestAccountingImportJobHttp', { businessId });
  return payload.job || null;
};

const getAccountingImportPackages = async ({ businessId = runtime.activeBusinessId } = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  const payload = await authorizedPost('getAccountingImportPackagesHttp', { businessId });
  return safeArray(payload.packages);
};

const deleteAccountingImportPackage = async ({ businessId = runtime.activeBusinessId, storagePath } = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  if (!storagePath) throw new Error(t('set.import.packages.deleteMissing'));
  return authorizedPost('deleteAccountingImportPackageHttp', { businessId, storagePath });
};

const downloadAccountingImportPackage = async ({ businessId = runtime.activeBusinessId, storagePath } = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  if (!storagePath) throw new Error(t('set.import.packages.downloadMissing'));
  return authorizedPost('downloadAccountingImportPackageHttp', { businessId, storagePath });
};

const removeAccountingImportYearData = async ({ businessId = runtime.activeBusinessId, year } = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  if (!/^\d{4}$/.test(String(year || ''))) throw new Error(t('set.import.years.invalid'));
  return authorizedPost('removeAccountingImportYearDataHttp', { businessId, year: String(year) });
};

const getAccountingImportSalesHistory = async ({
  businessId = runtime.activeBusinessId,
  year,
  query = '',
  page = 1,
  pageSize = 10,
  compact = false,
} = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  const parsedAmountQuery = window.PlummeListSearch?.parseAmountQuery?.(query);
  return authorizedPost('getAccountingImportSalesHistoryHttp', {
    businessId,
    year: String(year || ''),
    query,
    amountQuery: parsedAmountQuery?.amount ?? null,
    amountQuerySigned: parsedAmountQuery?.signed === true,
    page,
    pageSize,
    compact,
  });
};

const settleAccountingImportOpenItem = async ({
  businessId = runtime.activeBusinessId,
  action = 'settle',
  kind,
  itemKey,
  amount,
  settledAt,
  note,
} = {}) => {
  if (!businessId) throw new Error(t('svc.pickBusiness'));
  const payload = await authorizedPost('settleAccountingImportOpenItemHttp', {
    businessId, action, kind, itemKey, amount, settledAt, note,
  });
  if (Array.isArray(payload.settlements) && runtime.appState) {
    runtime.appState = mergeState({
      ...runtime.appState,
      accountingImportOpenItemSettlements: payload.settlements,
    });
    emitRuntime();
  }
  return payload;
};

const settleAccountingImportSalesHistoryDocument = async ({
  businessId = runtime.activeBusinessId,
  action = 'settle',
  documentKey,
  settledAt,
  note,
} = {}) => {
  if (!businessId) throw new Error(t('svc.pickBusiness'));
  const payload = await authorizedPost('settleAccountingImportSalesHistoryDocumentHttp', {
    businessId, action, documentKey, settledAt, note,
  });
  if (Array.isArray(payload.settlements) && runtime.appState) {
    runtime.appState = mergeState({
      ...runtime.appState,
      accountingImportSalesHistorySettlements: payload.settlements,
    });
    emitRuntime();
  }
  return payload;
};

const reanalyzeAccountingImportJob = async ({ businessId = runtime.activeBusinessId, jobId, onProgress } = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  if (!jobId) throw new Error(t('svc.missingImportJob'));
  onProgress?.({ phase: 'analyzing', percent: 100 });
  const payload = await authorizedPost('reanalyzeAccountingImportJobHttp', { businessId, jobId }, {
    authTimeoutMs: 30000,
    requestTimeoutMs: 570000,
    timeoutMessage: t('svc.reanalysisTimedOut'),
  });
  onProgress?.({ phase: 'analyzed', percent: 100 });
  return payload.job || null;
};

const commitAccountingImportJob = async ({ businessId = runtime.activeBusinessId, jobId, complianceAnswersByYear = {}, selectedImportYears, bankAccountSetup = {}, productCollisionImportKeys = [], invoiceNumberCollisionAccepted = false } = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  if (!jobId) throw new Error(t('svc.missingImportJob'));
  const payload = await authorizedPost('commitAccountingImportJobHttp', { businessId, jobId, complianceAnswersByYear, selectedImportYears, bankAccountSetup, productCollisionImportKeys, invoiceNumberCollisionAccepted });
  return payload.job || null;
};

const openAccountingImportDocument = async ({ businessId = runtime.activeBusinessId, document } = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  const payload = await authorizedPost('openAccountingImportDocumentHttp', {
    businessId,
    archiveStoragePath: document?.archiveStoragePath,
    sourcePath: document?.sourcePath,
  });
  return payload.document || null;
};

const uploadAccountingImportZip = async ({ businessId = runtime.activeBusinessId, file, files, source = 'complete-accounting-export', dataOnly = false, onProgress } = {}) => {
  const selectedFiles = safeArray(files).length ? safeArray(files) : (file ? [file] : []);
  if (!selectedFiles.length) throw new Error(t('svc.pickExportFile'));
  onProgress?.({ phase: 'validating', percent: 0 });
  const prepared = dataOnly
    ? await window.PlummeAccountingImportClientFilter.prepareAccountingImportFiles({
        files: selectedFiles,
        dataOnly: true,
        onProgress: (progress) => onProgress?.({ ...progress, dataOnly: true }),
      })
    : { files: selectedFiles, stats: null };
  const inputFiles = prepared.files;
  const totalBytes = inputFiles.reduce((sum, item) => sum + Number(item?.size || 0), 0);
  const uploadedFiles = [];
  let completedBytes = 0;
  for (let index = 0; index < inputFiles.length; index += 1) {
    const currentFile = inputFiles[index];
    const uploadedFile = await uploadAccountingImportFile({
      businessId,
      file: currentFile,
      onProgress: (progress) => {
        const bytesTransferred = completedBytes + Number(progress?.bytesTransferred || 0);
        const percent = totalBytes > 0 ? Math.max(0, Math.min(100, Math.round((bytesTransferred / totalBytes) * 100))) : progress?.percent || 0;
        onProgress?.({
          ...progress,
          fileIndex: index + 1,
          fileCount: inputFiles.length,
          fileName: inputFiles.length === 1 ? currentFile?.name : `${index + 1}/${inputFiles.length}: ${currentFile?.name || 'importfil'}`,
          bytesTransferred,
          totalBytes,
          percent,
        });
      },
    });
    completedBytes += Number(currentFile?.size || 0);
    uploadedFiles.push(uploadedFile);
  }
  const job = await createAccountingImportJob({
    businessId,
    file: uploadedFiles[0],
    files: uploadedFiles,
    source: dataOnly ? 'complete-accounting-export-data-only' : source,
    onProgress,
  });
  return job ? { ...job, clientFilterStats: prepared.stats, importMode: dataOnly ? 'data-only' : 'complete' } : job;
};

const accountingExportYears = (state = {}) => {
  const years = new Set([
    String(Number(state.activeYear) || ''),
    ...Object.keys(state.openingBalanceByYear || {}),
    ...Object.keys(state.yearEndEntriesByYear || {}),
    ...Object.keys(state.accountingImportReportStatsByYear || {}),
  ]);
  ['invoices', 'expenses', 'otherSales', 'payments', 'mileageEntries', 'payrollRuns', 'customsDeclarations'].forEach((key) => {
    safeArray(state[key]).forEach((entry) => {
      const raw = String(entry?.date || entry?.createdAt || entry?.period || entry?.year || '');
      const match = /(?:^|\D)(20\d{2})(?:\D|$)/.exec(raw);
      if (match) years.add(match[1]);
    });
  });
  return [...years].filter((year) => /^20\d{2}$/.test(year)).sort();
};

const buildCompleteExportSaftFiles = (state = {}) => {
  if ((state.settings?.country || 'NO') !== 'NO' || !window.PlummeSettingsDataTools?.buildSaftFinancialXml) return [];
  const allVouchers = getAccountingLedgerVouchersModel({ allYears: true });
  return accountingExportYears(state).flatMap((year) => {
    try {
      const result = window.PlummeSettingsDataTools.buildSaftFinancialXml(
        { ...state, activeYear: Number(year) },
        allVouchers,
        { includePriorForPartyBalances: true },
      );
      return result?.xml ? [{ year, xml: result.xml }] : [];
    } catch (error) {
      throw new Error(t('svc.saftYearGenerationFailed', { year, error: error?.message || t('svc.unknownError') }));
    }
  });
};

const createCompleteAccountingExport = async ({ businessId = runtime.activeBusinessId } = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  const state = mergeState(runtime.appState);
  const payload = await authorizedPost('createCompleteAccountingExportHttp', {
    businessId,
    saftFiles: buildCompleteExportSaftFiles(state),
  });
  return payload.job || null;
};

const getLatestCompleteAccountingExport = async ({ businessId = runtime.activeBusinessId } = {}) => {
  if (!businessId) throw new Error(t('svc.missingActiveBusiness'));
  const payload = await authorizedPost('getLatestCompleteAccountingExportHttp', { businessId });
  return payload.job || null;
};
